"use client";

import { PersonIcon } from "@navikt/aksel-icons";
import { PageContainer, RootPages } from "@navikt/dinesykmeldte-sidemeny";
import type { ReactElement } from "react";
import PageSideMenu from "../../../../components/PageSideMenu/PageSideMenu";
import PageError from "../../../../components/shared/errors/PageError";
import SykmeldtNotFound from "../../../../components/shared/errors/SykmeldtNotFound";
import ListSectionSkeleton from "../../../../components/shared/skeletons/ListSectionSkeleton";
import SkeletonRegion from "../../../../components/shared/skeletons/SkeletonRegion";
import SykmeldingerList from "../../../../components/sykmeldinger/SykmeldingerList";
import {
  createSykmeldingerBreadcrumbs,
  useUpdateBreadcrumbs,
} from "../../../../hooks/useBreadcrumbs";
import useFocusRefetch from "../../../../hooks/useFocusRefetch";
import { useSykmeldt } from "../../../../hooks/useSykmeldt";
import { fnrText, formatNameSubjective } from "../../../../utils/sykmeldtUtils";

function Sykmeldinger(): ReactElement {
  const { sykmeldtId, sykmeldt, isLoading, error, sykmeldtNotFound, refetch } =
    useSykmeldt();
  const sykmeldtName = formatNameSubjective(sykmeldt?.navn);

  useFocusRefetch(refetch);
  useUpdateBreadcrumbs(
    () => createSykmeldingerBreadcrumbs(sykmeldtId, sykmeldt?.navn),
    [sykmeldt?.navn, sykmeldtId],
  );

  return (
    <PageContainer
      header={{
        Icon: PersonIcon,
        title: `Sykmeldinger for ${sykmeldtName}`,
        subtitle: sykmeldt && fnrText(sykmeldt.fnr),
        subtitleSkeleton: !error && !sykmeldtNotFound,
      }}
      sykmeldt={sykmeldt}
      navigation={
        !sykmeldtNotFound && (
          <PageSideMenu
            activePage={RootPages.Sykmeldinger}
            sykmeldt={sykmeldt}
          />
        )
      }
    >
      {isLoading && !sykmeldt && (
        <SkeletonRegion loadingText="Laster sykmeldinger">
          <ListSectionSkeleton />
        </SkeletonRegion>
      )}
      {sykmeldt && (
        <SykmeldingerList sykmeldtId={sykmeldtId} sykmeldt={sykmeldt} />
      )}
      {error && !sykmeldtNotFound && (
        <PageError
          text="Vi klarte ikke å laste sykmeldingene"
          cause={error.message}
        />
      )}
      {sykmeldtNotFound && !error && <SykmeldtNotFound />}
    </PageContainer>
  );
}

export default Sykmeldinger;
